import React, { useState, useMemo } from 'react';
import { 
  DollarSign, Receipt, TrendingUp, FileText, Download, Filter, Calendar,
  BarChart3, Package, Tag, Percent
} from 'lucide-react';
import { 
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
  subWeeks, subMonths, format, isWithinInterval, startOfDay, endOfDay
} from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const InvoiceReport = ({ proposals = [], products = [] }) => {
  const { user } = useAuth();
  
  // Verificar si el usuario tiene permiso para ver márgenes y costos
  const canViewMargins = useMemo(() => {
    if (!user || !user.category || !user.category.permissions) {
      return false;
    }
    return user.category.permissions['reports.billing.margins'] === true;
  }, [user]);

  const [dateFilter, setDateFilter] = useState('currentMonth');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');

  // Estados de propuestas a incluir (Facturado, Pendiente de Cobro, Pagado Anticipo, Pago Total)
  const targetStatuses = ['Facturado', 'Pendiente de Cobro', 'Pagado Anticipo', 'Pago Total'];

  // Calcular rango de fechas según el filtro
  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (dateFilter) {
      case 'currentWeek':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        const lastWeekDate = subWeeks(now, 1);
        start = startOfWeek(lastWeekDate, { weekStartsOn: 1 });
        end = endOfWeek(lastWeekDate, { weekStartsOn: 1 });
        break;
      case 'currentMonth':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'lastMonth':
        const lastMonthDate = subMonths(now, 1);
        start = startOfMonth(lastMonthDate);
        end = endOfMonth(lastMonthDate);
        break;
      case 'q1':
        start = startOfQuarter(new Date(now.getFullYear(), 0, 1));
        end = endOfQuarter(new Date(now.getFullYear(), 0, 1));
        break;
      case 'q2':
        start = startOfQuarter(new Date(now.getFullYear(), 3, 1));
        end = endOfQuarter(new Date(now.getFullYear(), 3, 1));
        break;
      case 'q3':
        start = startOfQuarter(new Date(now.getFullYear(), 6, 1));
        end = endOfQuarter(new Date(now.getFullYear(), 6, 1));
        break;
      case 'q4':
        start = startOfQuarter(new Date(now.getFullYear(), 9, 1));
        end = endOfQuarter(new Date(now.getFullYear(), 9, 1));
        break;
      case 'custom':
        if (customDateStart && customDateEnd) {
          start = startOfDay(new Date(customDateStart));
          end = endOfDay(new Date(customDateEnd));
        } else {
          start = startOfMonth(now);
          end = endOfMonth(now);
        }
        break;
      default:
        start = startOfMonth(now);
        end = endOfMonth(now);
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const { start: dateStart, end: dateEnd } = getDateRange();

  // Filtrar propuestas por estado y fecha
  const filteredProposals = useMemo(() => {
    const filtered = proposals.filter(proposal => {
      // Filtrar por estado
      if (!targetStatuses.includes(proposal.status)) {
        return false;
      }

      // Filtrar por fecha (usar approvedDate o created_at)
      const proposalDate = proposal.approvedDate 
        ? new Date(proposal.approvedDate)
        : (proposal.approved_date 
          ? new Date(proposal.approved_date)
          : (proposal.created_at 
            ? new Date(proposal.created_at) 
            : (proposal.createdAt ? new Date(proposal.createdAt) : null)));
      
      if (!proposalDate || isNaN(proposalDate.getTime())) {
        return isWithinInterval(new Date(), { start: dateStart, end: dateEnd });
      }

      return isWithinInterval(proposalDate, { start: dateStart, end: dateEnd });
    });
    
    return filtered;
  }, [proposals, dateStart, dateEnd]);

  // Crear mapa de productos por ID
  const productsMap = useMemo(() => {
    const map = {};
    products.forEach(product => {
      const id = product.id?.toString();
      if (id) {
        map[id] = product;
        map[parseInt(id)] = product;
      }
    });
    return map;
  }, [products]);

  // Calcular costo total de una propuesta (desde items estructurados)
  const calculateProposalCost = (proposal) => {
    const items = proposal.items || [];
    if (items.length === 0) return 0;

    let totalCost = 0;
    items.forEach(item => {
      const productId = item.product_id;
      if (productId) {
        const productIdStr = productId.toString();
        const productIdNum = parseInt(productId);
        const product = productsMap[productIdStr] || productsMap[productIdNum] || productsMap[productId];
        if (product && product.cost) {
          const quantity = parseInt(item.quantity) || 0;
          totalCost += product.cost * quantity;
        }
      }
    });
    return totalCost;
  };

  // Calcular estadísticas totales
  const totals = useMemo(() => {
    let totalInvoiced = 0;
    let totalCost = 0;

    filteredProposals.forEach(proposal => {
      const amount = parseFloat(proposal.totalAmount) || 0;
      totalInvoiced += amount;
      totalCost += calculateProposalCost(proposal);
    });

    const costPercentage = totalInvoiced > 0 ? (totalCost / totalInvoiced) * 100 : 0;
    const margin = totalInvoiced - totalCost;
    const marginPercentage = totalInvoiced > 0 ? (margin / totalInvoiced) * 100 : 0;

    return {
      totalInvoiced,
      totalCost,
      costPercentage,
      margin,
      marginPercentage
    };
  }, [filteredProposals, productsMap]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="w-4 h-4 inline mr-1" />
              Período
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            >
              <option value="currentWeek">Semana Actual</option>
              <option value="lastWeek">Semana Pasada</option>
              <option value="currentMonth">Mes Actual</option>
              <option value="lastMonth">Mes Pasado</option>
              <option value="q1">Q1</option>
              <option value="q2">Q2</option>
              <option value="q3">Q3</option>
              <option value="q4">Q4</option>
              <option value="custom">Rango Personalizado</option>
            </select>
          </div>
          {dateFilter === 'custom' && (
            <>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Desde
                </label>
                <input
                  type="date"
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hasta
                </label>
                <input
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Estadísticas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center gap-3 mb-2">
            <Receipt className="w-6 h-6 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Total Facturado</span>
          </div>
          <p className="text-3xl font-bold text-blue-900">{formatCurrency(totals.totalInvoiced)}</p>
          <p className="text-xs text-gray-600 mt-1">{filteredProposals.length} cotización(es)</p>
        </div>

        {canViewMargins && (
          <>
            <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center gap-3 mb-2">
                <Package className="w-6 h-6 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">Costo Total</span>
              </div>
              <p className="text-3xl font-bold text-purple-900">{formatCurrency(totals.totalCost)}</p>
            </div>

            <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
              <div className="flex items-center gap-3 mb-2">
                <Percent className="w-6 h-6 text-orange-600" />
                <span className="text-sm font-medium text-gray-700">Costo %</span>
              </div>
              <p className="text-3xl font-bold text-orange-900">{totals.costPercentage.toFixed(2)}%</p>
            </div>

            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Margen</span>
              </div>
              <p className="text-3xl font-bold text-green-900">{formatCurrency(totals.margin)}</p>
            </div>

            <div className="bg-teal-50 rounded-xl p-6 border border-teal-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-teal-600" />
                <span className="text-sm font-medium text-gray-700">Margen %</span>
              </div>
              <p className="text-3xl font-bold text-teal-900">{totals.marginPercentage.toFixed(2)}%</p>
            </div>
          </>
        )}
      </div>

      {/* Tabla de cotizaciones */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Cotizaciones Facturadas
          </h3>
          <span className="text-sm text-gray-600">{filteredProposals.length} cotización(es)</span>
        </div>

        {filteredProposals.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay cotizaciones facturadas en el período seleccionado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID Cotización</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Total Facturado</th>
                  {canViewMargins && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Costo Total</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Costo %</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Margen</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Margen %</th>
                    </>
                  )}
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredProposals.map((proposal, index) => {
                  const cost = calculateProposalCost(proposal);
                  const amount = parseFloat(proposal.totalAmount) || 0;
                  const costPercentage = amount > 0 ? (cost / amount) * 100 : 0;
                  const margin = amount - cost;
                  const marginPercentage = amount > 0 ? (margin / amount) * 100 : 0;
                  const proposalDate = proposal.approvedDate 
                    ? new Date(proposal.approvedDate)
                    : (proposal.approved_date 
                      ? new Date(proposal.approved_date)
                      : (proposal.created_at ? new Date(proposal.created_at) : new Date()));

                  return (
                    <tr key={proposal.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">
                        {proposal.proposalId || proposal.proposal_id}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-800 font-semibold">
                        {formatCurrency(amount)}
                      </td>
                      {canViewMargins && (
                        <>
                          <td className="py-3 px-4 text-sm text-gray-700">
                            {formatCurrency(cost)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">
                            {costPercentage.toFixed(2)}%
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">
                            {formatCurrency(margin)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">
                            {marginPercentage.toFixed(2)}%
                          </td>
                        </>
                      )}
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {format(proposalDate, 'dd/MM/yyyy')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceReport;

